// @ts-check
import { useMemo } from 'react';
import ModelType from '../../Model/ModelType';
import { useTelemetryPushService } from 'services/TelemetryApi/useTelemetryPushV2';
import T from '../../../localization/LocalizationKeys';
import _ from 'lodash';
import NodeDataSource from './NodeDataSource';
import { assetsFromNodes } from './util/SignalSourceFunctions';
import useMeasurementSystem from 'components/Value/useMeasurementSystem';

const SignalRDataSource =
  ({
    nodes: nodesFromDatasource,
    signals = []
  }) => {
    const { nodes, isLoading: isLoadingNodes } = NodeDataSource({ nodes: nodesFromDatasource });
    const [assetIdToNode, filterOptions] = useMemo(() => {
      const assets = assetsFromNodes(nodes);
      const ids = _.map(assets, asset => asset.id);
      const _assetIdToNode = _.reduce(assets, (result, node) => {
        if (node) {
          return { ...result, [node.id]: node };
        }
        return result;
      }, {});
      return [_assetIdToNode, { assetIds: ids }];
    }, [nodes]);

    const updatedSignals = useMemo(() => {
      return _.map(signals, signal => {
        return {
          ...signal,
          componentName: (signal.component === '.') ? null : signal.component
        };
      });
    }, [signals]);

    const [filteredTelemetries, isLoading] = useTelemetryPushService({ filterOptions, telemetryModels: updatedSignals });
    const { convertToUserPairArray } = useMeasurementSystem({ });
    return useMemo(() => {
      const consolidated = _.compact(_.map(filteredTelemetries, dataSignal => {
        if (_.isEmpty(dataSignal?.dataPoints)) {
          return null;
        }
        const [dataPoints, schemaUnit] = convertToUserPairArray({ values: dataSignal.dataPoints, schemaUnit: { unit: dataSignal.unit, schema: dataSignal.type } });
        return {
          assetId: dataSignal.assetId,
          assetDisplayName: assetIdToNode[dataSignal.assetId]?.displayName,
          assetPath: '',
          componentName: dataSignal.componentName,
          signalId: dataSignal.signalId,
          signalDisplayName: dataSignal.signalDisplayName,
          type: schemaUnit.schema,
          unit: schemaUnit.unit,
          aggregate: dataSignal.aggregate,
          decimalPoint: dataSignal.decimalPoint,
          dataPoints
        };
      }));
      return {
        signals: updatedSignals,
        isLoading: isLoading || isLoadingNodes,
        values: filteredTelemetries,
        signalData: consolidated
      };
    }, [filteredTelemetries, updatedSignals, isLoading, isLoadingNodes, convertToUserPairArray, assetIdToNode]);
  };

SignalRDataSource.models = [
  ...NodeDataSource.models,
  {
    path: 'signals',
    type: ModelType.SIGNALS,
    label: T.common.signals
  }
];

SignalRDataSource.getUsedAssets = (data) => {
  return NodeDataSource.getUsedAssets(data);
};

export default SignalRDataSource;
